import { Filter, FilterConfig, FilterTypes, IsFilterable } from "@/lib/filterable";
import {
  MrfiktivInvoiceBalanceTotalViewModelGen,
  MrfiktivInvoiceBalanceViewModelGen
} from "@/services/mrfiktiv/v1/data-contracts";

@IsFilterable
class InvoiceBalanceTotalBase implements MrfiktivInvoiceBalanceTotalViewModelGen {
  /**
   * Currency code
   * @example EUR
   */
  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.invoiceBalanceTotal.currency"
  })
  currency: string;

  /**
   * Revenue ("Einnahmen"): outbound invoices plus absolute inbound credit notes
   * @example 330
   */
  @FilterConfig({
    type: FilterTypes.NUMBER,
    displayName: "objects.invoiceBalanceTotal.revenue"
  })
  revenue: number;

  /**
   * Expense ("Ausgaben"): inbound invoices plus absolute outbound credit notes
   * @example 120
   */
  @FilterConfig({
    type: FilterTypes.NUMBER,
    displayName: "objects.invoiceBalanceTotal.expense"
  })
  expense: number;

  /**
   * Balance ("Saldo"): revenue minus expense
   * @example 210
   */
  @FilterConfig({
    type: FilterTypes.NUMBER,
    displayName: "objects.invoiceBalanceTotal.balance"
  })
  balance: number;

  constructor(invoiceBalanceTotal?: Partial<MrfiktivInvoiceBalanceTotalViewModelGen>) {
    this.currency = invoiceBalanceTotal?.currency || "";
    this.revenue = invoiceBalanceTotal?.revenue || 0;
    this.expense = invoiceBalanceTotal?.expense || 0;
    this.balance = invoiceBalanceTotal?.balance || 0;
  }
}

type IInvoiceBalanceTotal = InvoiceBalanceTotalBase;
const InvoiceBalanceTotal = Filter.createForClass(InvoiceBalanceTotalBase);

@IsFilterable
class InvoiceBalanceBase implements MrfiktivInvoiceBalanceViewModelGen {
  /**
   * Total number of invoices across all currencies
   */
  @FilterConfig({
    type: FilterTypes.NUMBER,
    displayName: "objects.invoiceBalance.count"
  })
  count?: number;

  /**
   * Array of balances by currency
   */
  @FilterConfig({
    type: InvoiceBalanceTotal,
    displayName: "objects.invoiceBalance.totals"
  })
  totals?: IInvoiceBalanceTotal[];

  constructor(invoiceBalance?: Partial<MrfiktivInvoiceBalanceViewModelGen>) {
    this.count = invoiceBalance?.count;
    this.totals = invoiceBalance?.totals?.map((total) => new InvoiceBalanceTotalBase(total));
  }

  /**
   * Aliases for the totals so each table column (revenue, expense, balance) has a unique value path
   */
  get revenueTotals() {
    return this.totals;
  }

  get expenseTotals() {
    return this.totals;
  }

  get balanceTotals() {
    return this.totals;
  }

  /**
   * The values of the totals, one per currency in the order of the totals, so a custom view can show each as a column
   */
  get currency(): string[] {
    return (this.totals ?? []).map((total) => total.currency);
  }

  get revenue(): number[] {
    return (this.totals ?? []).map((total) => total.revenue);
  }

  get expense(): number[] {
    return (this.totals ?? []).map((total) => total.expense);
  }

  get balance(): number[] {
    return (this.totals ?? []).map((total) => total.balance);
  }
}

type IInvoiceBalance = InvoiceBalanceBase;
const InvoiceBalance = Filter.createForClass(InvoiceBalanceBase);

export { IInvoiceBalanceTotal, InvoiceBalanceTotal, IInvoiceBalance, InvoiceBalance };
